import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../context/LanguageContext';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send,
  MessageSquare,
  Calendar,
  Globe,
  CheckCircle,
  XCircle,
  X
} from 'lucide-react';
import { ArrowUp } from 'lucide-react';

const Contact = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    language: '',
    courseType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  // Toast state
  const [toast, setToast] = useState({ show: false, type: 'success', message: '' });

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
  };

  // Auto-hide toast after 4s
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
    return () => clearTimeout(t);
  }, [toast.show]);

  // Show back-to-top after scrolling a bit
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Build payload aligned with PHP sendmail.php
    const { name, email, phone, language, courseType, message } = formData;
    const payload = { name, email, phone, language, courseType, message };
    // Resolve API base if provided (e.g., https://yourdomain.com)
    const API_BASE = process.env.NEXT_PUBLIC_PHP_API_BASE || '';
    const url = `${API_BASE}/api/sendmail.php`;

    try {
      setIsSubmitting(true);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // try to parse JSON response
      let data = null;
      try { data = await res.json(); } catch (_) {}

      if (res.ok && data && data.ok) {
        showToast('success', 'Message sent successfully.');
        // Optionally reset form
        setFormData({ name: '', email: '', phone: '', language: '', courseType: '', message: '' });
      } else {
        const errText = (data && (data.error || data.message)) || `Request failed (${res.status})`;
        showToast('error', errText);
      }
    } catch (err) {
      showToast('error', 'Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t('visitSchool'),
      details: 'Avenida Minas Gerais,\n6-A 2780-024 OEIRAS',
      link: 'https://maps.app.goo.gl/ffepcSjJFWQHUGtDA',
      linkText: 'Get Directions'
    },
    {
      icon: Phone,
      title: t('callUs'),
      details: `+351 936 551 878\n${t('openingHours')}`,
      link: 'tel:+351 936 551 878',
      linkText: t('callNow')
    },
    {
      icon: Mail,
      title: t('emailUs'),
      details: `yorkschool.oeiras@gmail.com\n${t('response24')}`,
      link: 'mailto:yorkschool.oeiras@gmail.com',
      linkText: t('sendEmail')
    },
  ];

  const languages = [t('nameen'), t('namefr'), t('nameit'), t('namees'), t('namede'), t('namept')];
  const courseTypes = [t('groupClasses'), t('privateLesson'), t('onlineClasses'), t('intensiveCourses')];

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-accent-500/10 text-accent-500 mb-6">
            <MessageSquare className="w-5 h-5 mr-2" />
            <span className="font-medium">{t('getInTouch')}</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold font-heading text-primary-500 mb-6">
            {t('contactTitle')}
            <span className="block text-accent-500">{t('contactSubtitle')}</span>
          </h2>
          
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            {t('contactDescription')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-3xl p-8 lg:p-10 text-white"
          >
            <h3 className="text-3xl font-bold mb-2">{t('bookConsultationForm')}</h3>
            <p className="text-primary-100 mb-8">
              {t('consultationDescription')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-primary-100 mb-2">
                    {t('parentName')} *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-primary-200 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all"
                    placeholder={t('fullName')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-100 mb-2">
                    {t('emailAddressform')} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-primary-200 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all"
                    placeholder={t('exampleEmail')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-100 mb-2">
                  {t('phoneNumberform')}
                </label>
                <input
                  type="tel"
                  name="phone" required pattern="^[+\d][\d ()-]{3,31}$"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-primary-200 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all"
                  placeholder={t('phoneNumberformnumber')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-primary-100 mb-2">
                    {t('languageInterest')}
                  </label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all"
                  >
                    <option value="" className="text-neutral-800">{t('selectLanguage')}</option>
                    {languages.map(lang => (
                      <option key={lang} value={lang} className="text-neutral-800">{lang}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-100 mb-2">
                    {t('preferredCourse')}
                  </label>
                  <select
                    name="courseType"
                    value={formData.courseType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all"
                  >
                    <option value="" className="text-neutral-800">{t('selectcourseType')}</option>
                    {courseTypes.map(type => (
                      <option key={type} value={type} className="text-neutral-800">{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-100 mb-2">
                  {t('tellUsGoals')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-primary-200 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all resize-none"
                  placeholder={t('tellUsGoalsDescription')}
                />
              </div>
<motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent-500 hover:bg-accent-600 text-white font-semibold py-4 rounded-2xl flex items-center justify-center space-x-2 transition-colors duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Send className="w-5 h-5" />
                <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
              </motion.button>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="space-y-8"
          >
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-neutral-100"
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <info.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-primary-500 mb-2">{info.title}</h4>
                    <p className="text-neutral-600 whitespace-pre-line mb-4">{info.details}</p>
                    <motion.a
                      href={info.link}
                      className="inline-flex items-center space-x-2 text-accent-500 hover:text-accent-600 font-semibold transition-colors"
                      whileHover={{ x: 5 }}
                    >
                      <span>{info.linkText}</span>
                      <Send className="w-4 h-4" />
                    </motion.a>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Quick Actions */}
            <motion.div
              className="bg-gradient-to-br from-neutral-50 to-white rounded-2xl p-6 border border-neutral-100"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <h4 className="text-xl font-bold text-primary-500 mb-4">{t('quickActions')}</h4>
              <div className="space-y-4">
                <motion.button
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-3 rounded-2xl flex items-center justify-center space-x-2 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => window.location.href = "#contact"}
                >
                  <Calendar className="w-5 h-5" />
                  <span>{t('scheduleVisit')}</span>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      {/* Toast notifications */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25 }}
            className={`fixed bottom-6 right-6 z-50 max-w-sm rounded-xl shadow-lg ring-1 ring-black/5 p-4 flex items-start space-x-3 ${toast.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}
            role="status"
            aria-live="polite"
          >
            {toast.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 flex-shrink-0 text-red-600" />
            )}
            <div className="flex-1 text-sm">{toast.message}</div>
            <button
              type="button"
              onClick={() => setToast(prev => ({ ...prev, show: false }))}
              className="ml-2 rounded-md p-1 hover:bg-black/5"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 left-6 z-40 rounded-full bg-primary-500 text-white p-3 shadow-lg hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-accent-500"
            aria-label="Back to top"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Contact;
